import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';

import { StatusCode } from '../enum/StatusCode';
import { ModalType } from '../enum/health-monitor/ModalType';
import { StorageKey } from '../enum/StorageKey';

import { API_URL, REQUST_TIMEOUT } from '../util/environment';
import { formattedErrorMessages, ModalConfig } from '../util/health-monitor/modal-config';
import { endpointsForSuccessModal } from '../util/url';

import store from '../store/store';
import { setModalType } from '../store/slice/health-monitor/modal';

import i18n from '../i18n/i18n';

import { ApiConsumer } from './api-consumer';
import { setItemToSessionStorage } from '../util/session-storage';

let axiosInstance: AxiosInstance | undefined;

let accessToken = '';
let refreshToken = '';

let isAlreadyRefreshingAccessToken = false;

export const getAxiosInstance = (): AxiosInstance => axiosInstance!;

const setAccessToken = (newAccessToken: string): void => {
  accessToken = newAccessToken;
};

const setRefreshToken = (newRefreshToken: string): void => {
  refreshToken = newRefreshToken;
};

const setErrorMessage = (message: string): void => {
  ModalConfig.error.message = i18n.t(`healthMonitor.errorResponseMessage.${message}`);
};

const checkIfErrorMessageIsFormatted = (error: any): void => {
  formattedErrorMessages.forEach((formattedMessage) => {
    if (error.response?.data?.errors) {
      if (formattedMessage === error.response.data.errors) {
        setErrorMessage(`${error.response.data.errors}`);
      }
    } else {
      setErrorMessage('genericError');
    }
  });
};

/**
 * Fallback refresh token endpoint — used when normal refresh fails.
 */
const fallbackRefreshAccessToken = async () => {
  const tempToken = sessionStorage.getItem(StorageKey.ACCESS_TOKEN)?.trim();

  if (!tempToken) {
    throw new Error('Access token missing in sessionStorage');
  }

  return await ApiConsumer.authenticateOnHealthMonitor(tempToken);
};

const refreshAccessToken = async (error: AxiosError): Promise<AxiosResponse<any, any> | undefined> => {
  try {
    const failedRequest = error.config;

    // Use refresh token as access token for first refresh call
    setAccessToken(refreshToken);

    let response;

    try {
      // -------------------------------------------------
      // PRIMARY REFRESH ENDPOINT
      // -------------------------------------------------
      response = await ApiConsumer.refreshAccessToken();
    } catch (primaryError) {
      console.warn('Primary refresh failed. Trying fallback refresh...', primaryError);

      try {
        // -------------------------------------------------
        // FALLBACK REFRESH ENDPOINT
        // -------------------------------------------------
        response = await fallbackRefreshAccessToken();
      } catch (fallbackError) {
        console.error('Fallback refresh also failed.', fallbackError);

        isAlreadyRefreshingAccessToken = false;

        ModalConfig.error.message = i18n.t(`healthMonitor.errorResponseMessage.accessToken`);
        store.dispatch(setModalType(ModalType.ERROR));

        return Promise.reject(error);
      }
    }

    // -------------------------------------------------
    // One of the refreshes succeeded
    // -------------------------------------------------
    setAccessToken(response.accessToken);
    setRefreshToken(response.refreshToken);

    setItemToSessionStorage(StorageKey.ACCESS_TOKEN_HM, response.accessToken);
    setItemToSessionStorage(StorageKey.REFRESH_TOKEN_HM, response.refreshToken);

    isAlreadyRefreshingAccessToken = false;

    // Retry original failed request
    if (failedRequest) {
      if (failedRequest.headers) {
        failedRequest.headers.Authorization = `Bearer ${response.accessToken}`;
      }

      return axios.request(failedRequest);
    }

    return Promise.reject(error);
  } catch (e) {
    isAlreadyRefreshingAccessToken = false;

    ModalConfig.error.message = i18n.t(`healthMonitor.errorResponseMessage.accessToken`);
    store.dispatch(setModalType(ModalType.ERROR));

    return Promise.reject(e);
  }
};

const isResponseWithSuccessModal = (response: AxiosResponse<any, any>): boolean => {
  let result = false;

  endpointsForSuccessModal.forEach((endpoint) => {
    if (response.config.url?.includes(endpoint)) {
      result = true;
    }
  });

  return result;
};

export const initInterceptors = (): void => {
  getAxiosInstance().interceptors.request.use(
    async (config) => {
      if (config && config.headers) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }

      return config;
    },
    (error) => {
      console.log(`Problem ${error}`);
      return Promise.reject(error);
    },
  );

  getAxiosInstance().interceptors.response.use(
    async (response) => {
      try {
        if (isResponseWithSuccessModal(response)) {
          ModalConfig.success.message = i18n.t(`healthMonitor.successResponseMessage.${response.data}`);
          store.dispatch(setModalType(ModalType.SUCCESS));
        }
      } catch (e) {
        console.log(e, 'Error while trying to display success modal!');
      }

      return response;
    },
    async (error) => {
      if (error.response?.status === StatusCode.UNAUTHORIZED) {
        if (!isAlreadyRefreshingAccessToken) {
          isAlreadyRefreshingAccessToken = true;
          return refreshAccessToken(error);
        }
      } else {
        try {
          checkIfErrorMessageIsFormatted(error);
        } catch (e) {
          setErrorMessage('genericError');
        }

        store.dispatch(setModalType(ModalType.ERROR));

        return Promise.reject(error);
      }
    },
  );
};

export const initAxios = (initialAccessToken: string, initialRefreshToken: string): void => {
  setAccessToken(initialAccessToken);
  setRefreshToken(initialRefreshToken);

  axiosInstance = axios.create({
    baseURL: API_URL.endsWith('/') ? API_URL : API_URL.concat('/'),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    timeout: REQUST_TIMEOUT,
  });

  initInterceptors();
};

export default axiosInstance;
